/* Gunnaro — product.js
   <gn-product>          variant picker → updates URL + re-renders price/availability/buy/plate via Section Rendering API
   <gn-gallery>          scroll-snap gallery with thumbnails, prev/next, keyboard and lightbox
   <gn-recommendations>  lazy-loads Shopify product recommendations */

if (!customElements.get('gn-product')) {
  customElements.define('gn-product', class extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.sectionId = this.dataset.section;
      // JS takes over from the no-JS <select>
      this.querySelectorAll('select[name="id"]').forEach((s) => { s.disabled = true; });
      this.querySelectorAll('[data-variant-id]').forEach((i) => { i.disabled = false; });
      this.addEventListener('change', (e) => { if (e.target.matches('.option input[type="radio"]')) this.onOptionChange(); });
      this.markAvailability();
    }

    selectedOptions() {
      return [...this.querySelectorAll('.option')].map((fs) => fs.querySelector('input:checked')?.value);
    }

    markAvailability() {
      const selected = this.selectedOptions();
      this.querySelectorAll('.option').forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const candidate = [...selected];
          candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => i === index || o === candidate[i]) && v.options[index] === input.value);
          input.closest('.option__value').classList.toggle('is-unavailable', !match || !match.available);
        });
        fs.querySelector('[data-option-selected]').textContent = selected[index] || '';
      });
    }

    async onOptionChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      this.markAvailability();
      const idInput = this.querySelector('[data-variant-id]');
      const submit = this.querySelector('.product-form__submit');
      if (!variant) {
        if (submit) { submit.disabled = true; submit.textContent = window.Gunnaro.strings.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      const url = `${this.dataset.url}?variant=${variant.id}`;
      window.history.replaceState({}, '', url);
      if (variant.featured_media) this.querySelector('gn-gallery')?.goToMedia(variant.featured_media.id);

      try {
        const doc = await window.Gunnaro.fetchSection(url, this.sectionId);
        ['availability', 'price', 'buy', 'plate', 'sticky'].forEach((key) => {
          const fresh = doc.querySelector(`[data-product-update="${key}"]`);
          const current = this.querySelector(`[data-product-update="${key}"]`);
          if (fresh && current) current.replaceWith(fresh);
        });
        const freshId = this.querySelector('[data-variant-id]');
        if (freshId) { freshId.disabled = false; freshId.value = variant.id; }
      } catch (e) {
        // Keep the old markup; the form still posts the correct variant id.
      }
    }
  });
}

/* Mobile sticky add-to-cart: visible once the main button is above the viewport.
   Position check on scroll (not IntersectionObserver) so fast flings/jumps can't skip it. */
(() => {
  let ticking = false;
  const update = () => {
    ticking = false;
    const main = document.querySelector('.product-form__submit, .reserve-trigger');
    const bar = document.querySelector('[data-sticky-atc]');
    if (!main || !bar) return;
    const show = main.getBoundingClientRect().bottom < 0;
    if (bar.classList.contains('is-visible') === show) return;
    bar.classList.toggle('is-visible', show);
    if (show) document.documentElement.style.setProperty('--sticky-atc-h', `${bar.offsetHeight}px`);
    bar.setAttribute('aria-hidden', String(!show));
    bar.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
  };
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  update();
})();

if (!customElements.get('gn-gallery')) {
  customElements.define('gn-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('.gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.counter = this.querySelector('[data-gallery-index]');
      this.index = 0;

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.galleryThumb))));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(this.slides.indexOf(entry.target)); });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((s) => io.observe(s));

      const sources = this.querySelector('[data-gallery-sources]');
      this.sources = sources ? JSON.parse(sources.textContent) : [];
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.querySelectorAll('[data-gallery-zoom]').forEach((b) => b.addEventListener('click', () => this.zoom(Number(b.dataset.galleryZoom))));
      this.lightbox?.addEventListener('click', (e) => { if (e.target === this.lightbox || e.target.matches('[data-lightbox-image]')) this.lightbox.close(); });
    }

    go(i) {
      const next = (i + this.slides.length) % this.slides.length;
      const slide = this.slides[next];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      this.setActive(next);
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => Number(s.dataset.mediaId) === Number(mediaId));
      if (i >= 0) this.go(i);
    }

    setActive(i) {
      if (i < 0) return;
      this.index = i;
      if (this.counter) this.counter.textContent = String(i + 1);
      this.thumbs.forEach((t, n) => {
        if (n === i) { t.setAttribute('aria-current', 'true'); t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }
        else t.removeAttribute('aria-current');
      });
    }

    zoom(i) {
      const src = this.sources[i];
      if (!src || !this.lightbox) return;
      const stage = this.lightbox.querySelector('[data-lightbox-stage]');
      const img = document.createElement('img');
      img.src = src;
      img.alt = this.slides[i].querySelector('img')?.alt || '';
      img.dataset.lightboxImage = '';
      stage.replaceChildren(img);
      this.lightbox.showModal();
      document.body.classList.add('is-locked');
      this.lightbox.addEventListener('close', () => document.body.classList.remove('is-locked'), { once: true });
    }
  });
}

if (!customElements.get('gn-recommendations')) {
  customElements.define('gn-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.children.length) return;
      const io = new IntersectionObserver(async ([entry], observer) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        try {
          const res = await fetch(this.dataset.url);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = doc.querySelector('gn-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        } catch (e) { /* stay empty */ }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  });
}

/* Reservation (regulated goods): sticky "Rezervovat" opens the form and scrolls to it */
document.addEventListener('click', (e) => {
  const link = e.target.closest('[data-reserve-open]');
  if (!link) return;
  const details = document.querySelector('[data-reserve]');
  if (!details) return;
  e.preventDefault();
  details.open = true;
  details.scrollIntoView({ behavior: 'smooth', block: 'center' });
  setTimeout(() => details.querySelector('input:not([type=hidden])')?.focus({ preventScroll: true }), 400);
});
